import React, { useState } from "react";
import {
  Navbar,
  Container,
  Offcanvas,
  Nav,
  Button,
  Card,
  Tab,
  Row,
  Col
} from "react-bootstrap";

/**
 * AppShellWithTabs_v01
 * - Header (sticky)
 * - Slide-out left nav (Offcanvas)
 * - Center content with 4 "tabs" (pills style, carded)
 * - Footer (sticky on content end)
 *
 * Requirements:
 * - Ensure Bootstrap CSS is imported once in your app entry:
 *     import "bootstrap/dist/css/bootstrap.min.css";
 */

type TabKey = "overview" | "features" | "pricing" | "faq";

export interface AppShellWithTabsProps {
  appTitle?: string;
  defaultTab?: TabKey;
}

export const React_Tabs_v01_NavHeaderFooter: React.FC<AppShellWithTabsProps> = ({
  appTitle = "React Tabs Demo",
  defaultTab = "overview",
}) => {
  const [showNav, setShowNav] = useState(false);
  const [activeKey, setActiveKey] = useState<TabKey>(defaultTab);

  const handleSelect = (eventKey: string | null) => {
    if (!eventKey) return;
    setActiveKey(eventKey as TabKey);
    setShowNav(false);
  };

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ background: "var(--bs-body-bg)" }}>
      {/* Top Header */}
      <Navbar bg="light" className="shadow-sm sticky-top">
        <Container fluid className="px-3">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              className="d-inline-flex d-lg-none"
              onClick={() => setShowNav(true)}
              aria-label="Open navigation"
            >
              {/* simple hamburger */}
              <span style={{ width: 18, height: 2, background: "currentColor", display: "block", marginBottom: 4 }} />
              <span style={{ width: 18, height: 2, background: "currentColor", display: "block", marginBottom: 4 }} />
              <span style={{ width: 18, height: 2, background: "currentColor", display: "block" }} />
            </Button>
            <Navbar.Brand className="fw-semibold m-0">{appTitle}</Navbar.Brand>
          </div>

          <div className="d-none d-lg-flex">
            <Nav activeKey={activeKey} onSelect={handleSelect} className="gap-2">
              <Nav.Link eventKey="overview">Overview</Nav.Link>
              <Nav.Link eventKey="features">Features</Nav.Link>
              <Nav.Link eventKey="pricing">Pricing</Nav.Link>
              <Nav.Link eventKey="faq">FAQ</Nav.Link>
            </Nav>
          </div>
        </Container>
      </Navbar>

      {/* Slide-out Left Nav (Offcanvas) */}
      <Offcanvas
        show={showNav}
        onHide={() => setShowNav(false)}
        placement="start"
        backdrop
        scroll
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-semibold">{appTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav activeKey={activeKey} onSelect={handleSelect} className="flex-column">
            <Nav.Link eventKey="overview">Overview</Nav.Link>
            <Nav.Link eventKey="features">Features</Nav.Link>
            <Nav.Link eventKey="pricing">Pricing</Nav.Link>
            <Nav.Link eventKey="faq">FAQ</Nav.Link>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content */}
      <Container fluid className="flex-grow-1 py-4 px-3 px-md-4">
        {/* Tabs-as-pills with carded body */}
        <Tab.Container activeKey={activeKey} onSelect={handleSelect}>
          <Row className="g-3">
            <Col lg={3} className="d-none d-lg-block">
              {/* Optional persistent left nav on large screens */}
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold">Navigation</Card.Header>
                <Card.Body className="p-0">
                  <Nav variant="pills" className="flex-column p-2" activeKey={activeKey}>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                    <Nav.Link eventKey="features">Features</Nav.Link>
                    <Nav.Link eventKey="pricing">Pricing</Nav.Link>
                    <Nav.Link eventKey="faq">FAQ</Nav.Link>
                  </Nav>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={9}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-0 pb-0">
                  <Nav
                    variant="pills"
                    className="flex-nowrap overflow-auto"
                    activeKey={activeKey}
                    onSelect={handleSelect}
                  >
                    <Nav.Item>
                      <Nav.Link eventKey="overview" className="fw-semibold">Overview</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="features" className="fw-semibold">Features</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="pricing" className="fw-semibold">Pricing</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="faq" className="fw-semibold">FAQ</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>

                <Card.Body className="pt-3">
                  <Tab.Content>
                    <Tab.Pane eventKey="overview">
                      <h5 className="mb-2">Overview</h5>
                      <p className="text-secondary">
                        A crisp, modern tab layout using react-bootstrap’s <code>Tab.Container</code> + <code>Nav</code> (pills variant).
                        The left nav is available as a slide-out on mobile and as a card on large screens.
                      </p>
                    </Tab.Pane>

                    <Tab.Pane eventKey="features">
                      <h5 className="mb-2">Key Features</h5>
                      <ul className="mb-0">
                        <li>Sticky header with responsive top nav</li>
                        <li>Slide-out left navigation (Offcanvas)</li>
                        <li>Carded tabs area with pill controls</li>
                        <li>Type-safe tab keys (TypeScript)</li>
                      </ul>
                    </Tab.Pane>

                    <Tab.Pane eventKey="pricing">
                      <h5 className="mb-2">Pricing</h5>
                      <div className="d-grid gap-2">
                        <Card className="border-0 shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <div>
                                <div className="fw-semibold">Starter</div>
                                <div className="text-secondary small">For trying things out</div>
                              </div>
                              <div className="fs-4">$0</div>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <div>
                                <div className="fw-semibold">Pro</div>
                                <div className="text-secondary small">Advanced features</div>
                              </div>
                              <div className="fs-4">$19/mo</div>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <div>
                                <div className="fw-semibold">Enterprise</div>
                                <div className="text-secondary small">Custom needs</div>
                              </div>
                              <div className="fs-5">Contact sales</div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    </Tab.Pane>

                    <Tab.Pane eventKey="faq">
                      <h5 className="mb-2">FAQ</h5>
                      <div className="mb-3">
                        <div className="fw-semibold">How do I customize the look?</div>
                        <div className="text-secondary">
                          Use Bootstrap utility classes or override styles with your own CSS.
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="fw-semibold">Can I control the active tab externally?</div>
                        <div className="text-secondary">
                          Yes—manage the <code>activeKey</code> state in a parent and pass it down.
                        </div>
                      </div>
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab.Container>
      </Container>

      {/* Footer */}
      <footer className="border-top py-3">
        <Container fluid className="px-3">
          <div className="text-center text-secondary small">
            © {new Date().getFullYear()} {appTitle}. All rights reserved.
          </div>
        </Container>
      </footer>

      {/* Minimal polish for pill tabs */}
      <style>{`
        .nav-pills .nav-link {
          border-radius: 9999px;
        }
        .nav-pills .nav-link.active {
          box-shadow: 0 0.25rem 0.75rem rgba(0,0,0,.08);
        }
      `}</style>
    </div>
  );
};

export default React_Tabs_v01_NavHeaderFooter;
